import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { 
  Container, 
  Card, 
  CardContent, 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Grid, 
  Select, 
  MenuItem 
} from "@mui/material";
import { DataService } from "src/services/data-service";
import GenericInput from "src/components/input-components/GenericTextInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImagePicker from "src/components/input-components/GenericImagePicker";

// Define TypeScript interfaces
interface FooterMenuItem {
  Text: string;
  Link: string;
}

interface SocialItem {
  Platform: string;
  Link: string;
  IconClass: string;
}

interface FooterConfig {
  LogoUrl: string;
  CopyrightText: string;
  Navigation: FooterMenuItem[];
  ContactPhone: string;
  ContactEmail: string;
  Social: SocialItem[];
}

// Default footer config in case no data is returned
const defaultFooter: FooterConfig = {
  LogoUrl: "",
  CopyrightText: "",
  Navigation: [],
  ContactPhone: "",
  ContactEmail: "",
  Social: []
};

// Social media platform options (with corresponding icon classes)
const socialPlatforms = [
  { platform: "Facebook", iconClass: "fab fa-facebook-f" },
  { platform: "X", iconClass: "fab fa-x-twitter" },
  { platform: "Twitter", iconClass: "fab fa-twitter" },
  { platform: "LinkedIn", iconClass: "fab fa-linkedin-in" },
  { platform: "Instagram", iconClass: "fab fa-instagram" },
  { platform: "YouTube", iconClass: "fab fa-youtube" },
  { platform: "GitHub", iconClass: "fab fa-github" },
];

const EditFooter: React.FC = () => {
  const [editedFooter, setEditedFooter] = useState<FooterConfig>(defaultFooter);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [imagePickerOpen, setImagePickerOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchFooter = async () => {
      setLoading(true);
      const res = await DataService.get("api/layout/footer");

      if (!res.ok) {
        toast.error("Failed to load footer data");
        setLoading(false);
        return;
      }

      const jsonData = await res.json();
      if (jsonData?.Data) {
        setEditedFooter(jsonData.Data);
      } else {
        toast.error("Invalid data format from API");
      }
      setLoading(false);
    };

    fetchFooter();
  }, []);

  // Generic handleChange function to handle changes for FooterConfig fields
  const handleChange = (
    field: keyof FooterConfig,
    index: number | null,
    key: string | null,
    eventOrValue: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    setEditedFooter((prevState) => {
      const updated = { ...prevState };
      let value: string = "";

      if (typeof eventOrValue === "string") {
        value = eventOrValue;
      } else if (eventOrValue?.target?.value !== undefined) {
        value = eventOrValue.target.value;
      }

      if (
        field === "LogoUrl" ||
        field === "CopyrightText" ||
        field === "ContactPhone" ||
        field === "ContactEmail"
      ) {
        (updated as any)[field] = value;
      } else if (field === "Navigation" && index !== null && key) {
        updated.Navigation = [...prevState.Navigation];
        updated.Navigation[index][key] = value;
      } else if (field === "Social" && index !== null && key) {
        updated.Social = [...prevState.Social];
        updated.Social[index][key] = value;

        // When a social platform is selected, set its corresponding icon class automatically.
        if (key === "Platform") {
          const selectedPlatform = socialPlatforms.find(
            (p) => p.platform === value
          );
          if (selectedPlatform) {
            updated.Social[index].IconClass = selectedPlatform.iconClass;
          }
        }
      }

      return updated;
    });
  };

  // Open the image picker modal for the logo selection
  const handleOpenImagePicker = () => {
    setImagePickerOpen(true);
  };

  // Handle image selection from the image picker
  const handleImageSelect = (imageUrl: string) => {
    setEditedFooter((prevState) => ({
      ...prevState,
      LogoUrl: imageUrl,
    }));
    setImagePickerOpen(false);
  };

  // Add a new navigation menu item
  const handleAddMenuItem = () => {
    setEditedFooter((prevState) => ({
      ...prevState,
      Navigation: [...prevState.Navigation, { Text: "", Link: "" }],
    }));
  };

  // Remove a navigation menu item
  const handleRemoveMenuItem = (index: number) => {
    setEditedFooter((prevState) => ({
      ...prevState,
      Navigation: prevState.Navigation.filter((_, i) => i !== index),
    }));
  };

  // Add a new social item
  const handleAddSocial = () => {
    setEditedFooter((prevState) => ({
      ...prevState,
      Social: [...prevState.Social, { Platform: "", Link: "", IconClass: "" }],
    }));
  };

  // Remove a social item
  const handleRemoveSocial = (index: number) => {
    setEditedFooter((prevState) => ({
      ...prevState,
      Social: prevState.Social.filter((_, i) => i !== index),
    }));
  };

  // Save the updated footer configuration
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        Order: 1,
        Type: "FooterV1",
        Data: editedFooter,
      };

      const res = await DataService.put("api/layout/footer", {
        config: JSON.stringify(payload),
      });

      if (!res.ok) {
        toast.error("Failed to update footer");
        return;
      }

      toast.success("Footer updated successfully!");
    } catch (error) {
      toast.error("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Helmet>
        <title>Edit Footer</title>
      </Helmet>
      <Card elevation={3} sx={{ p: 3 }}>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSave}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <Typography variant="h5" gutterBottom align="center">
              Footer Configuration
            </Typography>

            {/* Logo Section */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6">Footer Logo</Typography>
              {editedFooter.LogoUrl && (
                <Box sx={{ mb: 1 }}>
                  <img
                    src={editedFooter.LogoUrl}
                    alt="Footer Logo"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
              <Button variant="outlined" onClick={handleOpenImagePicker}>
                Select Logo
              </Button>
            </Box>

            {/* Navigation Links Section */}
            <Typography variant="h6">Footer Links</Typography>
            {editedFooter.Navigation.map((item, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={5}>
                  <GenericInput
                    type="text"
                    title="Link Text"
                    value={item.Text}
                    onChange={(e) =>
                      handleChange("Navigation", index, "Text", e)
                    }
                    name=""
                  />
                </Grid>
                <Grid item xs={5}>
                  <GenericInput
                    type="text"
                    title="Link URL"
                    value={item.Link}
                    onChange={(e) =>
                      handleChange("Navigation", index, "Link", e)
                    }
                    name=""
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveMenuItem(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddMenuItem}
            >
              Add Footer Link
            </Button>

            {/* Copyright Text Section */}
            <GenericInput
              type="text"
              title="Copyright Text"
              value={editedFooter.CopyrightText}
              onChange={(e) =>
                handleChange("CopyrightText", null, null, e)
              }
              name="copyright"
            />

            {/* Contact Information Section */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <GenericInput
                  type="text"
                  title="Phone"
                  value={editedFooter.ContactPhone}
                  onChange={(e) =>
                    handleChange("ContactPhone", null, null, e)
                  }
                  name="phone"
                />
              </Grid>
              <Grid item xs={6}>
                <GenericInput
                  type="text"
                  title="Email"
                  value={editedFooter.ContactEmail}
                  onChange={(e) =>
                    handleChange("ContactEmail", null, null, e)
                  }
                  name="email"
                />
              </Grid>
            </Grid>

            {/* Social Links Section */}
            <Typography variant="h6">Social Links</Typography>
            {editedFooter.Social.map((item, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={4}>
                  <Select
                    value={item.Platform}
                    onChange={(e) =>
                      handleChange("Social", index, "Platform", e.target.value)
                    }
                    fullWidth
                  >
                    {socialPlatforms.map((option) => (
                      <MenuItem key={option.platform} value={option.platform}>
                        {option.platform}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <GenericInput
                    type="text"
                    title="Social Link"
                    value={item.Link}
                    onChange={(e) =>
                      handleChange("Social", index, "Link", e)
                    }
                    name={`social-${index}`}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSocial(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSocial}
            >
              Add Social Link
            </Button>

            {/* Save Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              sx={{ mt: 2 }}
            >
              {submitting ? "Saving..." : "Save Footer"}
            </Button>
          </Box>
        </CardContent>
      </Card>
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Container>
  );
};

export default EditFooter;
